import { useState, useEffect, useRef } from "react";
import { Check, Sparkles, Users, Plus, Clock, Calculator, Gift } from "lucide-react";

const grundpakkeIncluded = [
    "1 administrator",
    "Op til 20 medarbejdere med app-adgang",
    "Brugere og rettigheder",
    "Kunder og kontaktpersoner",
    "Kalender og opgaver",
    "Dokumenter og noter",
    "Basic dashboard",
    "Søgning",
];

// Brugere inkluderet i Cleany Core (trækkes fra brugerprisen)
const INCLUDED = {
    admin: 1,
    servicechef: 0,
    medarbejder: 20,
    kunde: 0,
};

const seatPrices = [
    { label: "Administrator", entry: 299, normal: 299 },
    { label: "Servicechef / Serviceleder", entry: 199, normal: 199 },
    { label: "Medarbejder (app-adgang)", entry: 39, normal: 39 },
    { label: "Kunde backend-adgang", entry: 19, normal: 19 },
];

const addonModules = [
    {
        name: "Drift & Kvalitet",
        entry: 495,
        normal: 1495,
        desc: "Kørsel, kundebesøg, kvalitetskontroller og reklamationer — alt med fuld sporbarhed.",
        includes: "Kørsel · Besøg & Kvalitet · Reklamationer",
    },
    {
        name: "Analyse & Rapportering",
        entry: 295,
        normal: 995,
        desc: "Statistikker, KPI-dashboards og rapporter over drift, kvalitet og økonomi.",
        includes: "Statistikker",
    },
    {
        name: "Adgang & Nøgle håndtering",
        entry: 295,
        normal: 695,
        desc: "Nøgledatabase med fuld sporbarhed — styr på kundenøgler, adgangsbrikker og koder.",
        includes: "Nøgledatabase",
    },
    {
        name: "Salg & Leads",
        entry: 295,
        normal: 795,
        desc: "Lead-pipeline, opfølgning og tilbud — konvertér interesserede til faste kunder.",
        includes: "Leads & Salg",
    },
    {
        name: "Lager & Vareflow",
        entry: 695,
        normal: 2195,
        desc: "Varekatalog, lager, bestillinger og anlægsarkiv — fuldt overblik over varer og maskiner.",
        includes: "Varekatalog & Lager · Anlægsarkiv",
    },
    {
        name: "Økonomi & Afregning",
        entry: 795,
        normal: 1995,
        desc: "Fakturering, self-billing og integration til e-conomic / Dinero.",
        includes: "Fakturering · Self-billing",
    },
];

const fmt = (n) => n.toLocaleString("da-DK");

export default function Pricing({ onCtaClick, hideCalculator = false }) {

    return (
        <section id="priser" className="py-24 md:py-32 bg-white" data-testid="pricing-section">
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-2 rounded-full bg-sky-50 border border-sky-100 px-3 py-1 text-xs font-semibold text-sky-700">
                            <span className="h-1.5 w-1.5 rounded-full bg-sky-500" /> Priser
                        </div>
                        <h2 className="mt-5 font-display text-3xl md:text-5xl font-bold tracking-tight text-slate-900">
                            Modulær prissætning. <br />
                            Du betaler kun for det du bruger.
                        </h2>
                        <p className="mt-4 text-base md:text-lg text-slate-600 leading-relaxed">
                            Én transparent månedspris — uden opsætningsgebyrer, uden binding og med fri opsigelse.
                        </p>
                        <div className="mt-5 inline-flex items-center gap-2.5 rounded-full bg-gradient-to-r from-emerald-50 to-sky-50 border border-emerald-200 px-4 py-2" data-testid="pricing-trial-badge">
                            <span className="inline-flex h-6 w-6 rounded-full bg-emerald-100 items-center justify-center">
                                <Gift className="h-3.5 w-3.5 text-emerald-700" />
                            </span>
                            <span className="text-sm font-semibold text-slate-800">
                                Prøv Cleany gratis i 14 dage
                            </span>
                            <span className="text-xs text-slate-500">
                                · ingen kreditkort · fuld adgang
                            </span>
                        </div>
                        <p className="mt-3 text-xs font-semibold text-slate-500 uppercase tracking-wider" data-testid="pricing-vat-notice">
                            Alle priser er ekskl. moms
                        </p>
                    </div>
                </div>

                {/* Top row: Grundpakke + Opstart */}
                <div className="mt-12 grid lg:grid-cols-3 gap-5">
                    {/* Grundpakke - large */}
                    <div className="lg:col-span-2 relative rounded-2xl border border-sky-300 bg-gradient-to-br from-sky-50/80 via-white to-white p-8 shadow-xl shadow-sky-900/5 ring-1 ring-sky-200" data-testid="pricing-grundpakke">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="inline-flex items-center gap-2 rounded-full bg-sky-600 text-white px-3 py-1 text-[11px] font-semibold">
                                    Grundpakke · Påkrævet
                                </div>
                                <h3 className="mt-3 font-display text-2xl font-bold text-slate-900">Cleany Core</h3>
                                <p className="mt-1 text-sm text-slate-600">Fundamentet — alt du behøver for at komme i gang.</p>
                            </div>
                        </div>
                        <div className="mt-6 flex items-end gap-3">
                            <span className="font-display text-6xl font-bold text-slate-900 tabular-nums">{fmt(1495)}</span>
                            <span className="mb-2 text-base text-slate-500">kr/md</span>
                        </div>
                        <div className="mt-6 grid sm:grid-cols-2 gap-2.5">
                            {grundpakkeIncluded.map((f) => (
                                <div key={f} className="flex items-start gap-2 text-sm text-slate-700">
                                    <Check className="h-4 w-4 mt-0.5 text-sky-600 shrink-0" />
                                    {f}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Opstart */}
                    <div className="relative rounded-2xl border border-slate-200 bg-white p-8" data-testid="pricing-opstart">
                        <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 text-slate-700 px-3 py-1 text-[11px] font-semibold">
                            Engangsbeløb
                        </div>
                        <h3 className="mt-3 font-display text-2xl font-bold text-slate-900">Opstart</h3>
                        <p className="mt-1 text-sm text-slate-600">Onboarding, opsætning og data-import.</p>
                        <div className="mt-6 flex items-end gap-3">
                            <span className="font-display text-5xl font-bold text-slate-900 tabular-nums">{fmt(2500)}</span>
                            <span className="mb-2 text-base text-slate-500">kr</span>
                        </div>
                        <ul className="mt-6 space-y-2.5 text-sm text-slate-700">
                            {["Skræddersyet opsætning", "Data-import fra Excel", "1:1 onboarding session", "Træning af nøglepersoner"].map((f) => (
                                <li key={f} className="flex items-start gap-2">
                                    <Check className="h-4 w-4 mt-0.5 text-emerald-500 shrink-0" />
                                    {f}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Brugere (seats) */}
                <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-8" data-testid="pricing-seats">
                    <div className="flex items-center gap-2 mb-1">
                        <Users className="h-5 w-5 text-sky-600" strokeWidth={1.75} />
                        <h3 className="font-display text-xl font-bold text-slate-900">Brugere</h3>
                    </div>
                    <p className="text-sm text-slate-600">Pris pr. bruger pr. måned. Skalerer med dit team.</p>
                    <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        {seatPrices.map((s) => (
                            <div key={s.label} className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                                <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">{s.label}</div>
                                <div className="mt-2 flex items-baseline gap-1">
                                    <span className="font-display text-3xl font-bold text-slate-900 tabular-nums">{fmt(s.entry)}</span>
                                    <span className="text-sm text-slate-500">kr/md</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Tillægsmoduler */}
                <div className="mt-10">
                    <div className="flex items-center gap-2 mb-1">
                        <Plus className="h-5 w-5 text-sky-600" strokeWidth={1.75} />
                        <h3 className="font-display text-xl font-bold text-slate-900">Tillægsmoduler</h3>
                    </div>
                    <p className="text-sm text-slate-600">Tænd kun de moduler du har brug for. Slå dem til og fra når som helst.</p>

                    <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="pricing-addons">
                        {addonModules.map((m) => (
                            <div
                                key={m.name}
                                className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-sky-300 transition-colors"
                                data-testid={`pricing-addon-${m.name.toLowerCase().replace(/[\s&]+/g, "-").replace(/^-|-$/g, "")}`}
                            >
                                <h4 className="font-display text-lg font-semibold text-slate-900">{m.name}</h4>
                                <p className="mt-1 text-sm text-slate-600 leading-relaxed">{m.desc}</p>
                                {m.includes && (
                                    <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold text-sky-700 bg-sky-50 border border-sky-100 rounded-full px-2 py-0.5">
                                        Indeholder: {m.includes}
                                    </div>
                                )}
                                <div className="mt-4 flex items-end gap-2">
                                    <span className="font-display text-3xl font-bold text-slate-900 tabular-nums">
                                        {fmt(m.entry)}
                                    </span>
                                    <span className="mb-1 text-sm text-slate-500">kr/md</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Example calculator */}
                {!hideCalculator && <ExampleCalc />}

                {/* CTA */}
                <div className="mt-12 text-center">
                    <button
                        onClick={onCtaClick}
                        data-testid="pricing-cta-main"
                        className="inline-flex items-center gap-2 rounded-full bg-sky-600 hover:bg-sky-700 text-white font-semibold px-8 h-13 py-4 shadow-xl shadow-sky-600/20 transition-all active:scale-[0.97]"
                    >
                        Start med 14 dages gratis prøveperiode
                    </button>
                    <div className="mt-3 text-xs text-slate-500">
                        Ingen kreditkort · Ingen binding · Opsig når som helst · Eksport altid gratis
                    </div>
                </div>
            </div>
        </section>
    );
}

export function ExampleCalc({ onChange } = {}) {
    const [seats, setSeats] = useState({
        admin: 1,
        servicechef: 4,
        medarbejder: 25,
        kunde: 0,
    });
    const [enabled, setEnabled] = useState({
        "Drift & Kvalitet": true,
        "Analyse & Rapportering": false,
        "Adgang & Nøgle håndtering": false,
        "Salg & Leads": false,
        "Lager & Vareflow": false,
        "Økonomi & Afregning": true,
    });

    const grundpakke = { entry: 1495, normal: 2995 };

    const billable = (k) => Math.max(0, seats[k] - INCLUDED[k]);

    const computeTotal = (tier) => {
        const seatTotal =
            billable("admin") * seatPrices[0][tier] +
            billable("servicechef") * seatPrices[1][tier] +
            billable("medarbejder") * seatPrices[2][tier] +
            billable("kunde") * seatPrices[3][tier];
        const addonTotal = addonModules.reduce(
            (sum, m) => sum + (enabled[m.name] ? m[tier] : 0),
            0,
        );
        return seatTotal + grundpakke[tier] + addonTotal;
    };

    const total = computeTotal("entry");

    // Report changes to an optional parent tracker (used by /beregn-pris).
    // Skips the initial render so we only fire on real user interaction.
    const firstRenderRef = useRef(true);
    useEffect(() => {
        if (firstRenderRef.current) {
            firstRenderRef.current = false;
            return;
        }
        if (typeof onChange === "function") onChange(total);
    }, [seats, enabled, total, onChange]);

    // Estimated time saved per week — ~30 min admin per medarbejder + flat overhead per modul
    const totalSeats = seats.admin + seats.servicechef + seats.medarbejder;
    const enabledCount = Object.values(enabled).filter(Boolean).length;
    const timeSavedHours = Math.round(totalSeats * 0.5 + enabledCount * 1.5 + 6);
    const timeSavedKr = timeSavedHours * 250 * 47; // 250 kr/t × 47 uger

    const seatTotal =
        billable("admin") * seatPrices[0]["entry"] +
        billable("servicechef") * seatPrices[1]["entry"] +
        billable("medarbejder") * seatPrices[2]["entry"] +
        billable("kunde") * seatPrices[3]["entry"];

    return (
        <div className="mt-10 rounded-2xl bg-gradient-to-br from-slate-900 to-sky-950 p-6 md:p-10 text-white relative overflow-hidden" data-testid="pricing-example">
            <div className="absolute inset-0 bg-grid opacity-10" />
            <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-sky-500/20 blur-3xl" />
            <div className="absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-cyan-400/15 blur-3xl" />

            <div className="relative">
                <div className="inline-flex items-center gap-2 rounded-full bg-sky-500/15 border border-sky-400/30 px-3 py-1 text-[11px] font-semibold text-sky-200">
                    <Calculator className="h-3.5 w-3.5" />
                    Beregn jeres pris
                </div>
                <h3 className="mt-3 font-display text-2xl md:text-3xl font-bold tracking-tight">
                    Få et estimat for jeres firma — på 30 sekunder.
                </h3>
                <p className="mt-2 text-sky-100/80 text-sm leading-relaxed max-w-2xl">
                    Indtast antallet af brugere, vælg modulerne I har brug for,
                    og se prisen med det samme.
                </p>
            </div>

            <div className="relative mt-8 grid lg:grid-cols-12 gap-6 items-start">
                {/* Inputs */}
                <div className="lg:col-span-7 space-y-5">
                    <div className="rounded-xl bg-white/5 backdrop-blur border border-white/10 p-5">
                        <div className="flex items-center gap-2 mb-4">
                            <Users className="h-4 w-4 text-sky-300" />
                            <div className="text-sm font-semibold text-white">Brugere</div>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                            <Stepper
                                label="Administrator"
                                value={seats.admin}
                                min={1}
                                onChange={(v) => setSeats({ ...seats, admin: v })}
                                hint={`299 kr/md · ${INCLUDED.admin} inkl.`}
                                included={INCLUDED.admin}
                                testid="calc-admin"
                            />
                            <Stepper
                                label="Servicechef / Serviceleder"
                                value={seats.servicechef}
                                onChange={(v) => setSeats({ ...seats, servicechef: v })}
                                hint="199 kr/md pr. stk"
                                testid="calc-servicechef"
                            />
                            <Stepper
                                label="Medarbejder (app-adgang)"
                                value={seats.medarbejder}
                                onChange={(v) => setSeats({ ...seats, medarbejder: v })}
                                hint={`39 kr/md · ${INCLUDED.medarbejder} inkl.`}
                                included={INCLUDED.medarbejder}
                                testid="calc-medarbejder"
                            />
                            <Stepper
                                label="Kunde backend-adgang"
                                value={seats.kunde}
                                onChange={(v) => setSeats({ ...seats, kunde: v })}
                                hint="19 kr/md pr. stk"
                                testid="calc-kunde"
                            />
                        </div>
                    </div>

                    <div className="rounded-xl bg-white/5 backdrop-blur border border-white/10 p-5">
                        <div className="flex items-center gap-2 mb-4">
                            <Plus className="h-4 w-4 text-sky-300" />
                            <div className="text-sm font-semibold text-white">Tillægsmoduler</div>
                            <div className="text-[11px] text-sky-200/60 ml-auto">
                                Cleany Core er altid inkluderet
                            </div>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-2.5">
                            {addonModules.map((m) => {
                                const isOn = enabled[m.name];
                                return (
                                    <button
                                        key={m.name}
                                        type="button"
                                        onClick={() => setEnabled({ ...enabled, [m.name]: !isOn })}
                                        data-testid={`calc-addon-${m.name.toLowerCase().replace(/[\s&]+/g, "-").replace(/^-|-$/g, "")}`}
                                        className={`flex items-start gap-3 text-left rounded-lg border p-3 transition-all ${
                                            isOn
                                                ? "bg-sky-500/15 border-sky-400/50 ring-1 ring-sky-400/40"
                                                : "bg-white/5 border-white/10 hover:border-white/20"
                                        }`}
                                    >
                                        <div
                                            className={`mt-0.5 h-4 w-4 rounded border flex items-center justify-center shrink-0 ${
                                                isOn ? "bg-sky-500 border-sky-400" : "border-white/30"
                                            }`}
                                        >
                                            {isOn && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm font-semibold text-white truncate">{m.name}</div>
                                            <div className="text-[11px] text-sky-100/70 mt-0.5 tabular-nums">
                                                {fmt(m.entry)} kr/md
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Price summary */}
                <div className="lg:col-span-5">
                    <PriceColumn
                        seats={seats}
                        seatTotal={seatTotal}
                        grundpakke={grundpakke.entry}
                        addons={addonModules.filter((m) => enabled[m.name]).map((m) => ({ name: m.name, price: m.entry }))}
                        total={total}
                    />
                </div>
            </div>

            {/* Time savings estimate */}
            <div className="relative mt-6">
                <div className="rounded-xl bg-gradient-to-r from-sky-500/15 to-cyan-500/15 border border-sky-400/30 p-5 flex flex-col gap-1 max-w-lg" data-testid="time-savings">
                    <div className="flex items-center gap-2.5">
                        <div className="h-9 w-9 rounded-full bg-sky-500/20 border border-sky-400/40 flex items-center justify-center text-sky-300">
                            <Clock className="h-4 w-4" />
                        </div>
                        <div>
                            <div className="text-sm font-semibold text-white">I sparer ca.</div>
                            <div className="text-[11px] text-sky-200/70">Estimeret tid på drift, fakturering & rapporter</div>
                        </div>
                    </div>
                    <div className="mt-1 flex items-baseline gap-2">
                        <div className="font-display text-3xl font-bold text-sky-200 tabular-nums">{fmt(timeSavedHours)} t</div>
                        <div className="text-sm text-sky-200/70">/uge</div>
                    </div>
                    <div className="text-[11px] text-sky-200/70 tabular-nums">≈ {fmt(timeSavedKr)} kr/år sparet i lønudgifter</div>
                </div>
            </div>
        </div>
    );
}

function Stepper({ label, value, onChange, hint, included = 0, min = 0, max = 9999, testid }) {
    const set = (v) => onChange(Math.max(min, Math.min(max, v)));
    const extra = Math.max(0, value - included);
    return (
        <div className="rounded-lg bg-white/5 border border-white/10 p-3">
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <div className="text-xs font-medium text-white truncate">{label}</div>
                    <div className="text-[10px] text-sky-200/60 mt-0.5">{hint}</div>
                </div>
                {included > 0 && extra > 0 && (
                    <span className="shrink-0 text-[9px] font-bold rounded-full bg-amber-500/20 text-amber-200 border border-amber-400/30 px-1.5 py-0.5">
                        +{extra} ekstra
                    </span>
                )}
                {included > 0 && extra === 0 && value <= included && (
                    <span className="shrink-0 text-[9px] font-bold rounded-full bg-emerald-500/15 text-emerald-200 border border-emerald-400/30 px-1.5 py-0.5">
                        inkluderet
                    </span>
                )}
            </div>
            <div className="mt-2 flex items-center gap-2">
                <button
                    type="button"
                    onClick={() => set(value - 1)}
                    disabled={value <= min}
                    data-testid={`${testid}-minus`}
                    className="h-8 w-8 rounded-md bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed text-white inline-flex items-center justify-center text-base"
                    aria-label="Træk fra"
                >
                    −
                </button>
                <input
                    type="number"
                    min={min}
                    max={max}
                    value={value}
                    onChange={(e) => set(parseInt(e.target.value) || 0)}
                    data-testid={testid}
                    className="flex-1 h-8 rounded-md bg-white/10 border border-white/10 text-center text-white font-semibold tabular-nums focus:outline-none focus:bg-white/20 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <button
                    type="button"
                    onClick={() => set(value + 1)}
                    data-testid={`${testid}-plus`}
                    className="h-8 w-8 rounded-md bg-white/10 hover:bg-white/20 text-white inline-flex items-center justify-center text-base"
                    aria-label="Læg til"
                >
                    +
                </button>
            </div>
        </div>
    );
}

function PriceColumn({ seats, seatTotal, grundpakke, addons, total }) {
    return (
        <div className="rounded-xl p-6 backdrop-blur bg-gradient-to-b from-sky-500/20 to-sky-500/5 border-2 border-sky-400/50">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-sky-200/80">Jeres pris</div>
            <div className="font-display text-base font-semibold text-white mt-0.5">Månedspris</div>

            <div className="mt-4 space-y-1.5 text-[12px]">
                {(() => {
                    const extraAdmin = Math.max(0, seats.admin - 1);
                    const extraMed = Math.max(0, seats.medarbejder - 20);
                    return (
                        <>
                            {extraAdmin > 0 && <Row l={`+${extraAdmin} ekstra administrator${extraAdmin > 1 ? "er" : ""}`} small />}
                            {seats.servicechef > 0 && <Row l={`${seats.servicechef} servicechef/leder`} small />}
                            {extraMed > 0 && <Row l={`+${extraMed} ekstra medarbejder${extraMed > 1 ? "e" : ""}`} small />}
                            {seats.kunde > 0 && <Row l={`${seats.kunde} kunde-adgang`} small />}
                            {extraAdmin === 0 && seats.servicechef === 0 && extraMed === 0 && seats.kunde === 0 && (
                                <Row l="Alle brugere inkluderet i Cleany Core" small />
                            )}
                            <div className="flex justify-between font-semibold text-white pt-1">
                                <span className="text-sky-200/70">Brugere i alt</span>
                                <span className="tabular-nums">{fmt(seatTotal)} kr</span>
                            </div>
                        </>
                    );
                })()}
            </div>

            <Divider />

            <div className="space-y-1.5 text-[12px]">
                <Row l="Cleany Core (inkl. 1 admin + 20 medarbejdere)" v={`${fmt(grundpakke)} kr`} small />
                {addons.map((a) => (
                    <Row key={a.name} l={a.name} v={`${fmt(a.price)} kr`} small />
                ))}
            </div>

            <Divider />

            <div className="pt-2">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-sky-200/70">Pr. måned</div>
                <div className="font-display text-3xl md:text-4xl font-bold tabular-nums leading-tight text-sky-200">
                    {fmt(total)} <span className="text-base font-medium text-sky-200/70">kr</span>
                </div>
                <div className="text-[10px] text-sky-200/60 tabular-nums mt-1">≈ {fmt(total * 12)} kr/år</div>
                <div className="text-[10px] text-sky-200/50 mt-1">Ekskl. moms</div>
            </div>
        </div>
    );
}

function Row({ l, v, small }) {
    return (
        <div className={`flex items-center justify-between ${small ? "text-sky-100/80" : "text-sky-100/90"}`}>
            <span className="truncate pr-2">{l}</span>
            {v && <span className="tabular-nums font-medium shrink-0">{v}</span>}
        </div>
    );
}
function Divider() {
    return <div className="border-t border-white/10 my-3" />;
}
